import { useEffect, useState, useRef, useCallback } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { InviteMemberModal } from './InviteStudentModal'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux/store'
import { GameRoomStatus } from '@/redux/types/room'
import RoomDetailsSkeleton from './RoomDetailsSkeleton'
import RoomNotFound from '../PageNotFound/RoomNotFound'

import { getRoomDetails, getRoomMembers, leaveProfessorGame } from '@/redux/asyncThunks/room'
import ROUTES from '@/routes/routes'

import RoomSummary from './RoomSummary'
import RoomButtons from './RoomButtons'
import { RoomHeader } from './RoomHeader'
import { ExportOptionsModal } from './ExportOptionsModal'
import { ImportModal } from './ImportModal'
import LeaveGameModal from './LeaveGameModal'
import { toast } from 'sonner'

const RoomDetails: React.FC = () => {
  const params = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const [isPolling, setIsPolling] = useState<boolean>(false)
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true)
  const [hasError, setHasError] = useState<boolean>(false)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const POLLING_INTERVAL = 5000

  const { currentRoom, isLoading, error } = useSelector(
    (state: RootState) => state.room
  )

  const [isInviteModalOpen, setIsInviteModalOpen] = useState<boolean>(false)
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false)
  const [importPlayersTeamsOpen, setImportPlayersTeamsOpen] =
    useState<boolean>(false)
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState<boolean>(false)
  const roomId = params.roomId as string

  const user = useSelector((state: RootState) => state.user.user)

  useEffect(() => {
    if (error) {
      setHasError(true)
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
        setIsPolling(false)
        console.log('Polling stopped due to error')
      }
    }
  }, [error])

  const fetchRoomData = useCallback(
    async (isPollingCall = false) => {
      if (roomId && !hasError) {
        try {
          await Promise.all([
            dispatch(getRoomDetails(roomId)),
            dispatch(getRoomMembers(roomId)),
          ])

          if (isInitialLoading && !isPollingCall) {
            setIsInitialLoading(false)
          }
        } catch (error) {
          console.error(
            `Failed to ${isPollingCall ? 'poll' : 'fetch'} room data:`,
            error
          )
          setHasError(true)
          if (isInitialLoading && !isPollingCall) {
            setIsInitialLoading(false)
          }
        }
      }
    },
    [dispatch, roomId, hasError, isInitialLoading]
  )

  useEffect(() => {
    if (!hasError) {
      fetchRoomData(false)
    }
  }, [roomId])

  useEffect(() => {
    if (!location.state || typeof location.state !== 'object') return

    const state = location.state as { openInvite?: boolean }
    if (!state.openInvite) return

    setIsInviteModalOpen(true)
    navigate(location.pathname, { replace: true })
  }, [location.pathname, location.state, navigate])

  const handleRefresh = () => {
    if (!hasError) {
      fetchRoomData(false)
    }
  }

  useEffect(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
      setIsPolling(false)
    }

    if (hasError || !currentRoom) return

    const shouldPoll =
      currentRoom.status === GameRoomStatus.NOT_STARTED &&
      !isInviteModalOpen &&
      !hasError

    if (shouldPoll) {
      setIsPolling(true)
      pollingIntervalRef.current = setInterval(() => {
        fetchRoomData(true)
      }, POLLING_INTERVAL)
      console.log('Polling started')
    } else {
      setIsPolling(false)
      console.log(
        'Polling stopped - Game ready, status changed, or error occurred'
      )
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
        setIsPolling(false)
        console.log('Polling cleaned up')
      }
    }
  }, [currentRoom, isInviteModalOpen, hasError])

  if (isInitialLoading && isLoading) {
    return <RoomDetailsSkeleton />
  }

  if (hasError || (error && !isInitialLoading)) {
    return (
      <div>
        <RoomNotFound message='Unable to fetch the room.' />
      </div>
    )
  }

  if (!currentRoom && !isInitialLoading) {
    return (
      <div>
        <RoomNotFound message='Unable to fetch the room.' />
      </div>
    )
  }

  if (!currentRoom) {
    return null
  }

  const isGameCompleted = currentRoom?.status === GameRoomStatus.COMPLETED
  const isOwner = currentRoom.owner === user?.email

  const handleLeaveGame = async (newOwnerId?: number) => {
    await dispatch(leaveProfessorGame({ roomId, newOwnerId })).unwrap()
    toast.success('You have left the game room.')
    navigate(ROUTES.PROFESSOR.ROOMS)
  }

  return (
    <div className='w-full px-24 py-5'>
      {isGameCompleted && (
        <div className='w-full p-4 mb-6 border-l-4 border-red-500 bg-red-50'>
          <p className='font-semibold text-red-700'>This Game Has Ended</p>
        </div>
      )}

      <RoomHeader
        roomName={currentRoom.roomname?.toString()}
        roomId={currentRoom.roomid}
        isGameCompleted={isGameCompleted}
        isPolling={isPolling}
        onRefresh={handleRefresh}
        onInviteClick={() => setIsInviteModalOpen(true)}
        onConfigureClick={() =>
          navigate(ROUTES.PROFESSOR.ROOM_CONFIGURE(roomId))
        }
        onExportClick={() => setIsExportModalOpen(true)}
        onImportPlayersTeamsClick={() => setImportPlayersTeamsOpen(true)}
        onLeaveClick={() => setIsLeaveModalOpen(true)}
      />

      <RoomButtons roomId={roomId} currentRoom={currentRoom} />

      <RoomSummary isPolling={isPolling} currentRoom={currentRoom} />

      <InviteMemberModal
        canInviteProfessors={true}
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
      />

      {isExportModalOpen && (
        <ExportOptionsModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          roomId={roomId}
          isLive={currentRoom.status === GameRoomStatus.IN_PROGRESS}
          gameMode={currentRoom.game_mode}
        />
      )}
      {importPlayersTeamsOpen && (
        <ImportModal
          key={`${roomId}-players_teams`}
          isOpen={importPlayersTeamsOpen}
          onClose={() => setImportPlayersTeamsOpen(false)}
          scope='players_teams'
          roomId={roomId}
          roomName={currentRoom.roomname?.toString()}
          onImportComplete={() => {
            setImportPlayersTeamsOpen(false)
            void fetchRoomData(false)
          }}
        />
      )}

      <LeaveGameModal
        isOpen={isLeaveModalOpen}
        onClose={() => setIsLeaveModalOpen(false)}
        onConfirm={handleLeaveGame}
        isOwner={isOwner}
        roomId={roomId}
      />
    </div>
  )
}

export default RoomDetails
